import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, BriefcaseBusiness, FileAudio, FileImage, FileText, FileVideo,
  GitCompareArrows, Scale, ShieldCheck, UploadCloud, ChevronDown,
  Scan, Cpu, Hash, Fingerprint, Brain, Zap, LogOut, User, History, Settings,
  Check, X, Loader2, Eye, AlertTriangle, Lock, Globe, ScanSearch, Radio, Sun, Camera,
} from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { ProAccessModal } from "../components/ProAccessModal";
import { ScanEyeLogoPrueba } from "../components/ScanEyeLogoPrueba";

const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "mp4", "mov", "avi", "webm", "mkv", "mp3", "wav", "ogg", "aac", "m4a", "pdf"];
const MAX_SIZE = 50 * 1024 * 1024;
const SCAN_DURATION_MS = 12000;

const sectors = [
  { value: "press", label: "Prensa" },
  { value: "insurance", label: "Seguros" },
  { value: "hr", label: "RR.HH." },
  { value: "business", label: "Empresa" },
  { value: "marketplace", label: "Marketplace" },
  { value: "osint", label: "OSINT" },
  { value: "general", label: "General" },
];

function getScanMessages(file, mode) {
  const isLegal = mode === "legal";
  const type = file.type || "";
  const ext = (file.name?.split(".").pop() || "").toLowerCase();
  if (type.startsWith("audio/") || ["mp3", "wav", "ogg", "aac", "m4a"].includes(ext)) return [
    isLegal ? "Calculando SHA-256 · MD5 · SHA-1 del archivo..." : "Verificación profesional: extrayendo espectro acústico...",
    "Detectando artefactos de síntesis y clonación de voz...",
    isLegal ? "Generando informe pericial PDF con sellado TSA RFC 3161..." : "Consolidando score de riesgo y descargando informe...",
  ];
  if (type === "application/pdf" || ext === "pdf") return [
    isLegal ? "Cadena de custodia: calculando hashes criptográficos..." : "Verificación documental: analizando estructura PDF...",
    "Verificando metadatos, capas XMP, modificaciones y firmas...",
    isLegal ? "Generando dictamen pericial con referencia SYE inmutable..." : "Consolidando análisis y preparando informe...",
  ];
  if (type.startsWith("video/") || ["mp4", "mov", "avi", "webm", "mkv"].includes(ext)) return [
    isLegal ? "Extrayendo fotogramas clave · Calculando hash del stream..." : "Verificación profesional: extrayendo fotogramas clave...",
    "Analizando coherencia temporal, ELA por frame y metadatos...",
    isLegal ? "Preparando dictamen de integridad de vídeo..." : "Consolidando señales de manipulación y deepfake...",
  ];
  return [
    isLegal ? "Motor Legal: calculando hashes SHA-256 · MD5 · SHA-1..." : "Motor Verify: escaneando con Gemini Vision + Sightengine...",
    isLegal ? "ELA · PRNU · JPEG Ghost · ORB · LSB Stego · Solar GPS · Alineación JPEG..." : "ELA · deepfake · GPS · ORB · LSB Stego · Solar GPS · Inpainting...",
    isLegal ? "Construyendo árbol de decisión legal · Generando informe PDF..." : "Calculando score de riesgo multi-señal · Generando informe PDF...",
  ];
}

export default function Home() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const fileInputRef = useRef(null);
  const referenceInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgIdx, setMsgIdx] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const [accessSource, setAccessSource] = useState("upgrade_modal");
  const [analysisMode, setAnalysisMode] = useState("legal");
  const [sector, setSector] = useState("general");
  const [selectedFile, setSelectedFile] = useState(null);
  const [referenceFile, setReferenceFile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const remaining = Math.max(0, (user?.free_scans_limit ?? 3) + (user?.bonus_scans ?? 0) - (user?.free_scans_used ?? 0));
  const totalAllowance = (user?.free_scans_limit ?? 3) + (user?.bonus_scans ?? 0);
  const isPro = user?.plan === "pro";

  // Plan visibility: 'legal', 'verify', 'both', 'pro', 'free' (undefined)
  const userPlan = user?.plan || "free";
  const hasLegal  = ["legal",  "both", "pro", "free"].includes(userPlan);
  const hasVerify = ["verify", "both", "pro", "free"].includes(userPlan);
  const onlyLegal  = hasLegal  && !hasVerify;
  const onlyVerify = hasVerify && !hasLegal;

  // Auto-select the only available mode
  useEffect(() => {
    if (onlyLegal)  setAnalysisMode("legal");
    if (onlyVerify) setAnalysisMode("verify");
  }, [onlyLegal, onlyVerify]);

  useEffect(() => {
    if (!scanning || messages.length === 0) return undefined;
    const interval = setInterval(() => setMsgIdx((i) => Math.min(i + 1, messages.length - 1)), 4000);
    return () => clearInterval(interval);
  }, [scanning, messages]);

  const validateFile = (file) => {
    if (!file) return "No se seleccionó ningún archivo.";
    if (file.size === 0) return "El archivo está vacío.";
    if (file.size > MAX_SIZE) return `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo: 50MB.`;
    const ext = (file.name?.split(".").pop() || "").toLowerCase();
    const okType = file.type.startsWith("image/") || file.type.startsWith("video/") || file.type.startsWith("audio/") || file.type === "application/pdf" || ALLOWED_EXT.includes(ext);
    if (!okType) return `Formato no soportado (${file.type || ext || "desconocido"}). Usa imagen, video, audio o PDF.`;
    return null;
  };

  const startScan = async () => {
    const file = selectedFile;
    if (!file) return toast.error("Selecciona primero una evidencia");
    if (!isPro && remaining <= 0) {
      setAccessSource("quota_exceeded");
      setShowAccess(true);
      return;
    }
    const err = validateFile(file) || (referenceFile ? validateFile(referenceFile) : null);
    if (err) return toast.error(err);
    if (file.type.startsWith("image/")) setPreviewUrl(URL.createObjectURL(file));
    else setPreviewUrl(null);
    setMessages(getScanMessages(file, analysisMode));
    setMsgIdx(0);
    setScanning(true);
    toast.message(analysisMode === "legal" ? "Generando análisis legal" : "Generando verificación profesional", { description: "Aplicando hash SHA-256, ELA, metadatos, IA y módulos adaptativos." });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("analysis_mode", analysisMode);
      fd.append("sector", analysisMode === "legal" ? "law" : sector);
      if (referenceFile) fd.append("reference_file", referenceFile);
      const [{ data }] = await Promise.all([
        api.post("/scans", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        new Promise((r) => setTimeout(r, SCAN_DURATION_MS)),
      ]);
      await refresh();
      navigate(`/app/resultado/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      if (e.response?.status === 402) {
        setAccessSource("quota_exceeded");
        setShowAccess(true);
      }
    } finally {
      setScanning(false);
      setPreviewUrl(null);
    }
  };

  const menuItems = [
    { label: "Historial", path: "/app/historial" },
    { label: "Pagos", action: () => { setAccessSource("payments"); setShowAccess(true); } },
    { label: "Configuración", path: "/app/perfil" },
    { label: "Soporte", action: () => toast.message("Soporte ScanEye", { description: "Escríbenos desde el correo asociado a tu cuenta." }) },
  ];

  const planAccent = analysisMode === "legal" ? "#818CF8" : "#DEFF9A";

  const engineModules = analysisMode === "legal"
    ? [
        { icon: Hash,             label: "SHA-256 · MD5 · SHA-1",          desc: "Integridad criptográfica triple" },
        { icon: Fingerprint,      label: "ELA + PRNU + JPEG Ghost",         desc: "Análisis forense de compresión y ruido" },
        { icon: Cpu,              label: "EXIF · XMP · cadena de custodia", desc: "Metadatos completos ISO 27037" },
        { icon: GitCompareArrows, label: "ORB Copy-Move (rotacional)",       desc: "Clones rotados, espejados y escalados" },
        { icon: Eye,              label: "Inpainting generativo",            desc: "Relleno IA: SD · DALL·E · Adobe Fill" },
        { icon: AlertTriangle,    label: "Timestamps imposibles",           desc: "Epoch Unix, fechas futuras, discrepancias" },
        { icon: ShieldCheck,      label: "Sellado TSA RFC 3161",            desc: "Sello de tiempo certificado" },
        { icon: ScanSearch,       label: "LSB Steganography",              desc: "Datos ocultos en el plano de bits LSB" },
        { icon: Scan,             label: "Alineación bloque JPEG",         desc: "Splicing por desalineación rejilla DCT" },
        { icon: Sun,              label: "Verificación Solar GPS+Hora",    desc: "Posición solar NOAA vs iluminación EXIF" },
        { icon: Camera,           label: "Identificación cámara (QT)",     desc: "Fabricante/software por tabla cuantización" },
        { icon: Zap,              label: "Informe pericial PDF firmado",    desc: "Gemini/Sightengine: solo contexto" },
      ]
    : [
        { icon: Scan,             label: "Mapa de calor ELA",              desc: "Regiones manipuladas en detalle" },
        { icon: Brain,            label: "Gemini Vision — 9 vectores",      desc: "IA multimodal forense" },
        { icon: Globe,            label: "Google Vision Web Detection",     desc: "Búsqueda inversa · fake sites · virales" },
        { icon: Cpu,              label: "Sightengine deepfake API",        desc: "Detección facial anti-deepfake" },
        { icon: Fingerprint,      label: "PRNU + JPEG Ghost",              desc: "Ruido de sensor e historial DCT" },
        { icon: GitCompareArrows, label: "ORB Copy-Move (rotacional)",      desc: "Clones geométricamente transformados" },
        { icon: Eye,              label: "Inpainting generativo",           desc: "Relleno IA por varianza de ruido" },
        { icon: ShieldCheck,      label: "GPS / timestamp coherence",       desc: "Cruce de metadatos de ubicación" },
        { icon: ScanSearch,       label: "LSB Steganography",              desc: "Datos ocultos en el plano de bits LSB" },
        { icon: Sun,              label: "Solar GPS+Hora",                 desc: "Posición solar vs iluminación" },
        { icon: Radio,            label: "ENF — Red Eléctrica (audio)",   desc: "Frecuencia de red 50/60Hz para detectar edición" },
      ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Subtle grid background */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px)", backgroundSize: "72px 72px" }} />

      {/* Ambient gradient orbs — Vercel-style */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="orb-float absolute rounded-full" style={{ width: 700, height: 700, top: -260, right: -180, background: 'radial-gradient(circle, rgba(0,87,255,0.07) 0%, transparent 65%)' }} />
        <div className="orb-float-2 absolute rounded-full" style={{ width: 560, height: 560, bottom: -180, left: -120, background: 'radial-gradient(circle, rgba(129,140,248,0.055) 0%, transparent 65%)' }} />
        <div className="orb-float-3 absolute rounded-full" style={{ width: 380, height: 380, top: '42%', left: '52%', transform: 'translateX(-50%)', background: analysisMode === 'legal' ? 'radial-gradient(circle, rgba(129,140,248,0.035) 0%, transparent 65%)' : 'radial-gradient(circle, rgba(222,255,154,0.03) 0%, transparent 65%)' }} />
      </div>

      {/* ── Header ── */}
      <header className="relative z-30 border-b border-white/[0.06] bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between gap-4">
          <ScanEyeLogoPrueba size="sm" />
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-[11px] text-white/40 font-mono">{user?.email}</span>
            <span className={`text-[10px] font-mono font-semibold px-2.5 py-1 rounded-full border ${isPro ? "text-[#DEFF9A] border-[#DEFF9A]/30 bg-[#DEFF9A]/8" : "text-white/50 border-white/10 bg-white/4"}`}>
              {isPro ? "PRO" : `${remaining}/${totalAllowance} scans`}
            </span>
            <div className="relative">
              <button onClick={() => setMenuOpen(v => !v)} className="w-8 h-8 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.1] transition-colors">
                <User size={13} className="text-white/60" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-10 w-48 bg-[#111] border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden z-50">
                  {menuItems.map(item => (
                    <button key={item.label} type="button"
                      onClick={() => { setMenuOpen(false); item.path ? navigate(item.path) : item.action?.(); }}
                      className="w-full text-left px-4 py-2.5 text-[12px] text-white/60 hover:text-white hover:bg-white/[0.05] transition-colors flex items-center gap-2.5">
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* ── Plan selector ── */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-mono text-white/30 uppercase tracking-widest">Motor de análisis</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">Selecciona el tipo de análisis</h1>
          <div className={`grid gap-4 ${hasLegal && hasVerify ? "sm:grid-cols-2" : "max-w-lg"}`}>
            {/* Legal card */}
            {hasLegal && (
              <button type="button" onClick={() => setAnalysisMode("legal")}
                className={`relative group text-left p-6 rounded-2xl border transition-all duration-200 ${analysisMode === "legal" ? "border-[#818CF8]/60 bg-[#818CF8]/[0.06]" : "border-white/[0.07] bg-white/[0.03] hover:border-white/[0.14]"}`}>
                {analysisMode === "legal" && <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ boxShadow: "0 0 0 1px rgba(129,140,248,0.3), inset 0 1px 0 rgba(129,140,248,0.1)" }} />}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#818CF8]/10 border border-[#818CF8]/20 flex items-center justify-center">
                    <Scale size={18} className="text-[#818CF8]" />
                  </div>
                  {analysisMode === "legal"
                    ? <span className="text-[10px] font-mono font-semibold text-[#818CF8] bg-[#818CF8]/10 px-2.5 py-1 rounded-full border border-[#818CF8]/20">Activo</span>
                    : onlyLegal && <span className="text-[10px] font-mono text-[#818CF8]/60 px-2.5 py-1">Tu plan</span>}
                </div>
                <div className="font-display text-xl font-bold text-white mb-1">Área Legal</div>
                <div className="text-[11px] font-mono text-[#818CF8] uppercase tracking-widest mb-3">Dictamen pericial · Cadena judicial</div>
                <p className="text-[13px] text-white/50 leading-relaxed mb-4">Para abogados y peritos informáticos. El informe generado es válido como prueba pericial: incluye hash SHA-256/MD5/SHA-1, sellado TSA RFC 3161, ELA cuantificada y cadena de custodia ISO/IEC 27037.</p>
                <ul className="space-y-1">
                  {["Hash triple: SHA-256 + MD5 + SHA-1","Sellado temporal TSA RFC 3161","ELA forense con mapa de error localizado","Informe pericial válido en expediente judicial","Cadena de custodia criptográfica"].map(f => (
                    <li key={f} className="flex items-center gap-2 text-[12px] text-white/40">
                      <span className="text-[#818CF8] text-[10px]">&#10003;</span>{f}
                    </li>
                  ))}
                </ul>
              </button>
            )}

            {/* Verify card */}
            {hasVerify && (
              <button type="button" onClick={() => setAnalysisMode("verify")}
                className={`relative group text-left p-6 rounded-2xl border transition-all duration-200 ${analysisMode === "verify" ? "border-[#DEFF9A]/40 bg-[#DEFF9A]/[0.04]" : "border-white/[0.07] bg-white/[0.03] hover:border-white/[0.14]"}`}>
                {analysisMode === "verify" && <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ boxShadow: "0 0 0 1px rgba(222,255,154,0.25), inset 0 1px 0 rgba(222,255,154,0.06)" }} />}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#DEFF9A]/10 border border-[#DEFF9A]/20 flex items-center justify-center">
                    <BriefcaseBusiness size={18} className="text-[#DEFF9A]" />
                  </div>
                  {analysisMode === "verify"
                    ? <span className="text-[10px] font-mono font-semibold text-[#DEFF9A] bg-[#DEFF9A]/10 px-2.5 py-1 rounded-full border border-[#DEFF9A]/20">Activo</span>
                    : onlyVerify && <span className="text-[10px] font-mono text-[#DEFF9A]/60 px-2.5 py-1">Tu plan</span>}
                </div>
                <div className="font-display text-xl font-bold text-white mb-1">Área Verify</div>
                <div className="text-[11px] font-mono text-[#DEFF9A] uppercase tracking-widest mb-3">Verificación profesional · Deepfake · Prensa</div>
                <p className="text-[13px] text-white/50 leading-relaxed mb-4">Para medios, seguros y empresas que verifican imágenes y documentos a diario. Detección deepfake con IA (Sightengine + Gemini Vision), mapa de calor, consistencia de iluminación y descarga directa del informe PDF.</p>
                <ul className="space-y-1">
                  {["Detección deepfake con Sightengine + Gemini IA","Búsqueda inversa Google Vision · fake sites","Mapa de calor de manipulación","Análisis de ruido sintético PRNU","Consistencia lumínica y geometría facial","Descarga PDF inmediata — sin validación de email"].map(f => (
                    <li key={f} className="flex items-center gap-2 text-[12px] text-white/40">
                      <span className="text-[#DEFF9A] text-[10px]">&#10003;</span>{f}
                    </li>
                  ))}
                </ul>
              </button>
            )}
          </div>
          {/* Upgrade hint when user only has one plan */}
          {(onlyLegal || onlyVerify) && (
            <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-white/25">
              <span>⚠️</span>
              <span>Solo tienes acceso al plan <strong className="text-white/40">{onlyLegal ? "Legal" : "Verify"}</strong>. Para añadir el otro módulo contacta con el equipo ScanEye.</span>
            </div>
          )}

          {/* Sector selector (Verify only) */}
          {analysisMode === "verify" && (
            <div className="mt-4 p-4 rounded-xl border border-white/[0.07] bg-white/[0.02]">
              <label className="text-[11px] font-mono text-white/40 uppercase tracking-widest block mb-2">Sector</label>
              <div className="flex flex-wrap gap-2">
                {sectors.map(s => (
                  <button key={s.value} type="button" onClick={() => setSector(s.value)}
                    className={`px-3.5 py-1.5 rounded-lg text-[12px] font-mono border transition-all ${sector === s.value ? "border-[#DEFF9A]/40 bg-[#DEFF9A]/10 text-[#DEFF9A]" : "border-white/[0.07] text-white/40 hover:text-white/70 hover:border-white/20"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Main grid: upload + modules ── */}
        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          {/* Upload zone */}
          <div className="space-y-4">
            <div
              onClick={() => !scanning && fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); if (!scanning) setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => {
                e.preventDefault(); setDragging(false);
                if (scanning) return;
                const f = e.dataTransfer.files?.[0];
                if (!f) return;
                const err = validateFile(f);
                if (err) return toast.error(err);
                setSelectedFile(f);
                if (f.type.startsWith('image/')) setPreviewUrl(URL.createObjectURL(f));
                else setPreviewUrl(null);
              }}
              className={`relative rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer overflow-hidden
                ${dragging ? 'bg-white/[0.04]' : selectedFile ? 'border-white/20 bg-white/[0.03]' : 'border-white/[0.09] bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'}
                ${scanning ? 'cursor-default' : ''}`}
              style={{ minHeight: '260px', borderColor: dragging ? `${planAccent}70` : undefined }}>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8">
                {scanning ? (
                  <div className="text-center w-full max-w-sm">
                    <Loader2 size={32} className="mx-auto mb-4 text-white/40 animate-spin" />
                    <div className="text-[11px] font-mono text-white/30 uppercase tracking-widest mb-2">
                      Fase {msgIdx + 1} / {messages.length}
                    </div>
                    <div className="font-display text-xl font-bold text-white leading-snug">
                      {messages[msgIdx] || "Inicializando motor forense..."}
                    </div>
                    <div className="mt-6 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-white/30 to-white/60 animate-pulse" style={{ width: `${((msgIdx + 1) / messages.length) * 100}%`, transition: "width 0.6s ease" }} />
                    </div>
                  </div>
                ) : selectedFile ? (
                  <>
                    {previewUrl
                      ? <img src={previewUrl} alt="preview" className="max-h-36 max-w-full rounded-lg object-contain opacity-80" />
                      : <div className="w-16 h-16 rounded-2xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center"><FileText size={28} className="text-white/40" /></div>
                    }
                    <div className="text-center">
                      <div className="text-[13px] font-semibold text-white truncate max-w-xs">{selectedFile.name}</div>
                      <div className="text-[11px] font-mono text-white/30 mt-0.5">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <button type="button" onClick={e => { e.stopPropagation(); setSelectedFile(null); setPreviewUrl(null); }}
                      className="text-[11px] font-mono text-white/30 hover:text-white/60 underline transition-colors">
                      Quitar archivo
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.07] flex items-center justify-center">
                      <UploadCloud size={24} className="text-white/40" />
                    </div>
                    <div className="text-center">
                      <div className="text-[15px] font-semibold text-white mb-1">Arrastra la evidencia aquí</div>
                      <div className="text-[12px] text-white/35">o haz clic para seleccionar un archivo</div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1.5">
                      {[[FileImage,"Imagen"],[FileVideo,"Vídeo"],[FileAudio,"Audio"],[FileText,"PDF"]].map(([Icon,label]) => (
                        <span key={label} className="flex items-center gap-1 text-[10px] font-mono text-white/25 border border-white/[0.06] px-2.5 py-1 rounded-full">
                          <Icon size={10} />{label}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Reference file (optional) */}
            <div className="flex items-center gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
              <GitCompareArrows size={14} className="text-white/30 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-mono text-white/30 uppercase tracking-widest">Archivo de referencia (opcional)</div>
                {referenceFile && <div className="text-[12px] text-white/60 truncate mt-0.5">{referenceFile.name}</div>}
              </div>
              <button onClick={() => referenceInputRef.current?.click()}
                className="text-[11px] font-mono text-white/40 border border-white/[0.08] px-3 py-1.5 rounded-lg hover:text-white/70 hover:border-white/20 transition-colors shrink-0">
                {referenceFile ? "Cambiar" : "Añadir"}
              </button>
            </div>

            {/* Scan button */}
            <button onClick={startScan} disabled={scanning || !selectedFile}
              className={`w-full py-4 rounded-2xl font-display text-[15px] font-bold tracking-wide transition-all duration-300 flex items-center justify-center gap-2 relative overflow-hidden
                ${scanning || !selectedFile
                  ? 'bg-white/[0.05] text-white/20 cursor-not-allowed border border-white/[0.05]'
                  : analysisMode === 'legal'
                    ? 'text-white shadow-lg shadow-[#818CF8]/25 hover:shadow-[#818CF8]/40'
                    : 'text-black shadow-lg shadow-[#DEFF9A]/20 hover:shadow-[#DEFF9A]/35'
                }`}
              style={!(scanning || !selectedFile) ? {
                background: analysisMode === 'legal'
                  ? 'linear-gradient(110deg, #6d7afa, #818CF8, #a5adff, #818CF8, #6d7afa)'
                  : 'linear-gradient(110deg, #c8ff6e, #DEFF9A, #f0ffbe, #DEFF9A, #c8ff6e)',
                backgroundSize: '200% auto',
                animation: 'shimmer 3s linear infinite',
              } : undefined}>
              {scanning
                ? <><Loader2 size={16} className="animate-spin" /> Analizando...</>
                : <><Scan size={16} /> Iniciar análisis {analysisMode === "legal" ? "legal" : "de verificación"}</>
              }
            </button>
          </div>

          {/* Engine modules panel */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: planAccent }} />
                <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: planAccent }}>
                  {analysisMode === "legal" ? "Motor Legal" : "Motor Verify"}
                </span>
              </div>
              <div className="font-display text-base font-bold text-white mb-4">Módulos activos</div>
              <div className="space-y-1.5">
                {engineModules.map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                    <Icon size={12} style={{ color: planAccent }} className="shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[12px] font-semibold text-white/70">{label}</div>
                      {desc && <div className="text-[10px] text-white/30 mt-0.5">{desc}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quota / plan info */}
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5">
              <div className="text-[11px] font-mono text-white/30 uppercase tracking-widest mb-3">Tu plan</div>
              {isPro ? (
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-[#DEFF9A]" />
                  <span className="text-[13px] font-semibold text-[#DEFF9A]">Pro — Análisis ilimitados</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[12px] text-white/50">Scans disponibles</span>
                    <span className="text-[12px] font-mono font-bold text-white">{remaining}/{totalAllowance}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden mb-3">
                    <div className="h-full rounded-full bg-[#DEFF9A]/70 transition-all" style={{ width: `${totalAllowance > 0 ? (remaining / totalAllowance) * 100 : 0}%` }} />
                  </div>
                  {remaining === 0 && (
                    <button onClick={() => { setAccessSource("quota_exceeded"); setShowAccess(true); }}
                      className="w-full text-[12px] font-mono text-black bg-[#DEFF9A] hover:bg-[#ecffb5] py-2 rounded-lg transition-colors font-semibold mt-1">
                      Ampliar acceso
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Full-screen scanning overlay ── */}
      {scanning && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-10"
          style={{ background: 'rgba(0,0,0,0.93)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)' }}>
          {/* Pulsing rings + scan icon */}
          <div className="relative flex items-center justify-center" style={{ width: 160, height: 160 }}>
            <div className="scan-ring absolute rounded-full" style={{ width: 80, height: 80, border: `1px solid ${planAccent}40` }} />
            <div className="scan-ring-2 absolute rounded-full" style={{ width: 80, height: 80, border: `1px solid ${planAccent}28` }} />
            <div className="scan-ring-3 absolute rounded-full" style={{ width: 80, height: 80, border: `1px solid ${planAccent}14` }} />
            <div className="relative z-10 flex items-center justify-center rounded-full" style={{ width: 72, height: 72, background: `${planAccent}0A`, border: `1px solid ${planAccent}30` }}>
              {/* Animated scan line inside icon */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div className="scan-line absolute left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${planAccent}CC, transparent)`, filter: `blur(1px)` }} />
              </div>
              <Scan size={28} style={{ color: planAccent }} />
            </div>
          </div>
          {/* Text block */}
          <div className="text-center max-w-xs px-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] mb-3" style={{ color: `${planAccent}55` }}>
              Motor forense activo &middot; Fase {msgIdx + 1}/{messages.length}
            </div>
            <div className="font-display text-[22px] font-bold text-white leading-snug mb-1">
              {messages[msgIdx] || 'Inicializando motor forense...'}
            </div>
            {selectedFile && (
              <div className="mt-2 text-[11px] font-mono truncate max-w-[230px] mx-auto" style={{ color: 'rgba(255,255,255,0.22)' }}>
                {selectedFile.name}
              </div>
            )}
            {/* Gradient progress bar */}
            <div className="mt-6 h-px rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-px rounded-full transition-all duration-1000 ease-out btn-shimmer"
                style={{
                  width: `${((msgIdx + 1) / messages.length) * 100}%`,
                  background: `linear-gradient(90deg, ${planAccent}00, ${planAccent}CC, ${planAccent}00)`,
                  backgroundSize: '200% auto',
                }} />
            </div>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" className="hidden" accept="image/*,video/*,audio/*,.pdf"
        onChange={e => { const f = e.target.files?.[0]; if (f) { setSelectedFile(f); if (f.type.startsWith("image/")) setPreviewUrl(URL.createObjectURL(f)); else setPreviewUrl(null); } e.target.value = ""; }} />
      <input ref={referenceInputRef} type="file" className="hidden" accept="image/*,video/*,audio/*,.pdf"
        onChange={e => { const f = e.target.files?.[0]; if (f) setReferenceFile(f); e.target.value = ""; }} />

      <ProAccessModal open={showAccess} onClose={() => setShowAccess(false)} source={accessSource} />
    </div>
  );
}

